/** Combat system: targeting, firing (single, chain, pierce, splash), auras, hits and damage with statuses and Mythic hooks. */
import { BossMechanic, DamageType, TowerArchetype, isAura, isBeam, isMortar } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { Enemy, Tower } from "../entities";
import { SimEventKind } from "../events";
import { TargetPriority } from "../command";
import { SimConstants } from "../constants";
import { distanceSquared } from "../../world/roadGeometry";
import { LevelSim } from "../levelSim";
import { maybeBoost, onBossHit, dropBomb, igniteBurn } from "./enemyMotion";
import { affixedDamage, affixedSlow, canBurn, splitOnDeath } from "../affixes";
import { mythicValue, spreadBurnOnDeath } from "../mythics";
import { isTargetable } from "./targeting";
import { stopBeam, tickBeam } from "./beam";
import { onHitByTower, resistedDamage } from "./enemyAbilities";
import { BASE_TIERS, slowAtTier } from "../branches/ascension";
import { auraBonusAtLevel, slowAtLevel } from "../levelGrowth";
import { branchAfterHit, branchHit, beginShot, endShot, seesBurrowed } from "../branches/hits";
import { afterShot, branchTurn, fireCone, secondBoltTarget, splashOf } from "../branches/shots";
import { clusterBomb, launchSalvo, rocketHitsAir } from "../branches/shells";
import { branchKillPoints, onBranchDeath } from "../branches/deaths";
import { auraGift } from "../branches/auras";
import { isLiveRare, onRareKilled } from "../rares";
import { isLiveWyrm, onWyrmKilled } from "../wyrm";
import { isLiveWildPet, onWildPetCaught } from "../wildPet";
import { petDamageTaken, petKillPoints } from "../petAura";
import { immuneToDamage, immuneToSlow, immunityForDamage, noteImmune } from "../immunity";
import { Immunity } from "../../data/spawnRules";
import { fireChain, firePierce } from "./combatLines";

export { fireChain, firePierce };

const MIN_ARMOUR = Fix64.parse("-0.5");

export function fireTowers(sim: LevelSim): void {
  for (const t of sim.towerSlots) {
    if (!t.alive) continue;
    if (t.disabledTicksLeft > 0) { t.disabledTicksLeft--; stopBeam(sim, t); continue; }
    const a = sim.balance.towers[t.archetype];
    // Arcane Spire: the beam's hold and ramp run every tick, its pulses on the fire interval (systems/beam.ts).
    if (isBeam(a)) { tickBeam(sim, t, a); continue; }
    // Tier 4 (sim/branches): the Tesla charges and discharges, the Blizzard works through its aura.
    if (t.tier > BASE_TIERS && branchTurn(sim, t, a)) continue;
    if (t.cooldown > 0) { t.cooldown--; continue; }
    // A Barracks fights through its soldiers (sim/barracks.ts), a Hex Totem through its curse (systems/hex.ts).
    if (isAura(a) || a.fireIntervalTicks <= 0 || a.soldiers > 0) continue;
    // Eagle Eye (tier 4, Marksman) finds a Burrower underground (ENEMIES_V2: the one targeting predicate's seesBurrowed).
    const target = findTarget(sim, t, t.tier > BASE_TIERS && seesBurrowed(sim, t));
    if (target === null) continue;
    t.cooldown = sim.fireIntervalOf(t);
    sim.events.add(SimEventKind.TowerFired, t.id, target.id);
    const damage = sim.damageOf(t);
    beginShot(sim, t);
    shoot(sim, t, a, target, damage);
    if (t.tier > BASE_TIERS) {
      const second = secondBoltTarget(sim, t, target);
      if (second !== null) { sim.events.add(SimEventKind.TowerFired, t.id, second.id); shoot(sim, t, a, second, damage); }
      afterShot(sim, t, target, damage);
    }
    endShot(sim);
  }
}

/** One shot of `t` at `target`: a shell or salvo, a pierce line, a flame cone, or a hit with its chain, Mythics and splash. */
function shoot(sim: LevelSim, t: Tower, a: TowerArchetype, target: Enemy, damage: Fix64): void {
  if (isMortar(a)) { launchSalvo(sim, t, a, target, damage); return; }
  const tx = target.x, ty = target.y;
  if (a.pierceCount > 0) { firePierce(sim, t, a, target, damage); return; }
  if (t.tier > BASE_TIERS && fireCone(sim, t, a, target, damage)) return;
  applyHit(sim, target, t, a, damage);
  if (a.chainTargets > 1) fireChain(sim, t, a, target, damage);
  // Mythic "Splitting Arrows": a chain-less tower's hit jumps once to the nearest other enemy within 2 cells for half damage.
  if (a.chainTargets <= 1 && sim.hasMythic(t, sim.mythicArcherChain)) {
    let best: Enemy | null = null; let bestD2 = Fix64.fromInt(4);
    for (const e of sim.enemySlots) {
      if (!e.alive || e === target || !canTarget(a, e) || !sim.targetable(e)) continue;
      const d2 = distanceSquared(tx, ty, e.x, e.y);
      if (d2.le(bestD2) && (best === null || d2.lt(bestD2) || e.id < best.id)) { best = e; bestD2 = d2; }
    }
    // ChainHit reads (enemy, tower, hop) as fireChain writes it; this one hop is hop 1.
    if (best !== null) { sim.events.add(SimEventKind.ChainHit, best.id, t.id, 1); sim.areaDepth++; applyDamage(sim, best, t, damage.divInt(2), a.damageType); sim.areaDepth--; }
  }
  const splash = t.tier > BASE_TIERS ? splashOf(sim, t, a) : a.splashRadius;
  if (splash.isPositive()) {
    const r2 = splash.mul(splash);
    const times = sim.hasMythic(t, sim.mythicCannonDoubleSplash) ? 2 : 1; // Mythic "Twin Shells"
    sim.areaDepth++;
    for (const e of sim.enemySlots) {
      if (!e.alive || e === target || !canTarget(a, e) || !sim.targetable(e)) continue;
      if (!distanceSquared(tx, ty, e.x, e.y).le(r2)) continue;
      // Frost (no damage of its own): the burst only chills. Shards, Shatter and the freezes stay on the struck enemy.
      if (a.damage.isZero()) { chill(sim, e, t, a); continue; }
      for (let k = 0; k < times && e.alive; k++) applyHit(sim, e, t, a, damage);
    }
    sim.areaDepth--;
    if (t.tier > BASE_TIERS) clusterBomb(sim, t, a, tx, ty, damage);
  }
}

/** The best target in range by the tower's priority; `seesBurrowed` lets a skill that "sees burrowed" find a Burrower underground. */
export function findTarget(sim: LevelSim, t: Tower, seesBurrowed = false): Enemy | null {
  const a = sim.balance.towers[t.archetype];
  const range = sim.rangeOf(t);
  const r2 = range.mul(range);
  // Mortar: a blind spot up close (minRange); zero elsewhere, so the check below never skips anything.
  const min2 = a.minRange.mul(a.minRange);
  const reach = range.ceilToInt() + 1;
  const tx = Fix64.fromInt(t.cell.x), ty = Fix64.fromInt(t.cell.y);
  let best: Enemy | null = null, wasted: Enemy | null = null;   // wasted: the best one it cannot hurt (M36 immune units)
  const air = t.tier > BASE_TIERS && rocketHitsAir(sim, t);   // tier 4: Seeker Shells hunt flyers too
  for (const e of sim.enemySlots) {
    if (!e.alive || !(canTarget(a, e) || (air && e.isAir)) || !isTargetable(sim, e, seesBurrowed)) continue;
    // Cheap integer prefilter (a superset of the exact check) keeps BigInt work off far enemies.
    if (Math.abs(e.cx - t.cell.x) > reach || Math.abs(e.cy - t.cell.y) > reach) continue;
    const d2 = distanceSquared(tx, ty, e.x, e.y);
    if (d2.gt(r2) || d2.lt(min2)) continue;
    if (e.immunity !== 0 && !affects(a, e)) { if (wasted === null || better(t.priority, e, wasted)) wasted = e; continue; }
    if (best === null || better(t.priority, e, best)) best = e;
  }
  return best ?? wasted;
}

/**
 * Whether `a`'s shots do anything to an immune unit (M36): its damage type, or for a damageless slowing tower (Frost)
 * its slow. A tower aims at an enemy it can hurt before one it cannot, and at an immune one only when nothing else is in
 * reach (the IMMUNE popup then shows the player why it is not dying).
 */
function affects(a: TowerArchetype, e: Enemy): boolean {
  if (a.damage.isZero() && a.slowFraction.isPositive()) return (e.immunity & Immunity.Slow) === 0;
  return !immuneToDamage(e, a.damageType);
}

/** The best target for `t` other than enemy `except` (the Twin Repeater's second bolt, the Sorcerer's extra beams). */
export function findTargetExcept(sim: LevelSim, t: Tower, ...except: number[]): Enemy | null {
  const a = sim.balance.towers[t.archetype];
  const range = sim.rangeOf(t);
  const r2 = range.mul(range), min2 = a.minRange.mul(a.minRange);
  const tx = Fix64.fromInt(t.cell.x), ty = Fix64.fromInt(t.cell.y);
  const air = t.tier > BASE_TIERS && rocketHitsAir(sim, t);
  let best: Enemy | null = null, wasted: Enemy | null = null;
  for (const e of sim.enemySlots) {
    if (!e.alive || except.includes(e.id) || !(canTarget(a, e) || (air && e.isAir)) || !sim.targetable(e)) continue;
    const d2 = distanceSquared(tx, ty, e.x, e.y);
    if (d2.gt(r2) || d2.lt(min2)) continue;
    if (e.immunity !== 0 && !affects(a, e)) { if (wasted === null || better(t.priority, e, wasted)) wasted = e; continue; }
    if (best === null || better(t.priority, e, best)) best = e;
  }
  return best ?? wasted;
}

export function recomputeAuras(sim: LevelSim): void {
  for (const t of sim.towerSlots) {
    if (!t.alive) continue;
    t.auraDamageMult = Fix64.One;
    t.auraPointsMult = Fix64.One;
    t.auraHaste = Fix64.Zero;
    t.auraRangeBonus = Fix64.Zero;
    for (const b of sim.towerSlots) {
      if (!b.alive || b === t) continue;
      const ba = sim.balance.towers[b.archetype];
      if (!isAura(ba)) continue;
      const r = sim.rangeOf(b);
      const d2 = distanceSquared(Fix64.fromInt(t.cell.x), Fix64.fromInt(t.cell.y), Fix64.fromInt(b.cell.x), Fix64.fromInt(b.cell.y));
      if (!d2.le(r.mul(r))) continue;
      // A tier-4 Beacon lends its branch's gifts (sim/branches/auras.ts): the Lighthouse's damage, War Banner haste, Fortune, Guiding Light.
      const gift = b.tier > BASE_TIERS ? auraGift(sim, b) : null;
      t.auraDamageMult = t.auraDamageMult.add(auraBonusAtLevel(ba, sim.towerLevels[b.archetype] ?? 1, gift?.damage ?? ba.auraDamageBonus));
      t.auraPointsMult = t.auraPointsMult.add(gift?.points ?? ba.auraPointsBonus);
      if (gift !== null) { t.auraHaste = Fix64.max(t.auraHaste, gift.haste); t.auraRangeBonus = Fix64.max(t.auraRangeBonus, gift.range); }
    }
  }
}

export function applyHit(sim: LevelSim, e: Enemy, source: Tower, a: TowerArchetype, damage: Fix64): void {
  if (source.tier > BASE_TIERS) damage = branchHit(sim, e, source, a, damage);   // tier-4 hit rules (sim/branches/hits.ts)
  if (a.burnDamagePerSecond.isPositive() && a.burnDurationTicks > 0 && canBurn(sim, e)) {
    let perTick = a.burnDamagePerSecond.mul(sim.damageOf(source)).div(Fix64.max(a.damage, Fix64.One)).divInt(SimConstants.TicksPerSecond);
    if (a.damage.isZero()) perTick = a.burnDamagePerSecond.divInt(SimConstants.TicksPerSecond);
    igniteBurn(e, source.id, perTick, a.burnDurationTicks, a.burnMaxStacks);
    sim.events.add(SimEventKind.EnemyBurning, e.id, source.id, a.burnDurationTicks);
  }
  // Mythic "Deep Rot": a higher shred cap.
  if (a.shredPerHit.isPositive() && !immuneToDamage(e, a.damageType) && e.shredStacks < a.shredMax + mythicValue(sim, source.archetype, sim.mythics.poisonShred).floorToInt()) {
    e.shredStacks++;
    e.shredArmour = e.shredArmour.add(a.shredPerHit);
    sim.events.add(SimEventKind.EnemyShredded, e.id, source.id, e.shredStacks);
  }
  if (chill(sim, e, source, a)) {
    // Mythic "Deep Freeze": every third hit from this tower type holds the enemy still for a second.
    if (sim.hasMythic(source, sim.mythicFrostFreeze) && (e.hitsTaken + 1) % 3 === 0) {
      e.slowFraction = affixedSlow(sim, e, Fix64.One); e.slowTicksLeft = SimConstants.TicksPerSecond;
      sim.events.add(SimEventKind.EnemySlowed, e.id, source.id, SimConstants.TicksPerSecond);
    }
  }
  if (damage.isPositive()) applyDamage(sim, e, source, damage, a.damageType);
  if (source.tier > BASE_TIERS) branchAfterHit(sim, e, source);                // Headshot, Annihilate
}

/** The tower's slow on one enemy (the Colossus shrugs it off); true when it took. Frost's splash is this alone. */
function chill(sim: LevelSim, e: Enemy, source: Tower, a: TowerArchetype): boolean {
  if (!a.slowFraction.isPositive() || a.slowDurationTicks <= 0 || sim.balance.enemies[e.archetype].bossMechanic === BossMechanic.Colossus) return false;
  if (immuneToSlow(e)) { noteImmune(sim, e, source, Immunity.Slow); return false; }   // M36 immune unit
  // the tier's slow (the Glacier's at tier 4), grown by the type's level (sim/levelGrowth.ts)
  const [fraction, ticks] = source.tier > BASE_TIERS ? slowAtTier(sim, source.archetype, source.tier)
    : slowAtLevel(a, sim.towerLevels[source.archetype] ?? 1, [a.slowFraction, a.slowDurationTicks]);
  const slow = affixedSlow(sim, e, fraction);                // Frostbound: half as slowed
  if (slow.gt(e.slowFraction)) e.slowFraction = slow;
  e.slowTicksLeft = ticks;
  sim.events.add(SimEventKind.EnemySlowed, e.id, source.id, ticks);
  return true;
}

/**
 * A heavy, large enemy (M30 #14): a heavy archetype (Brute, Stunner) or any boss but a rare spawn (Kevin 2026-10-25: area
 * damage hits a rare in full). Area damage does less to it.
 */
export function isHeavy(sim: LevelSim, e: Enemy): boolean { return (e.isBoss && !e.isRare) || sim.balance.enemies[e.archetype].heavy; }

/** Whether this hit is area damage (M30 #14): dealt inside an area loop, or by an area tower (Cannon, Mortar, Lightning). */
export function isAreaHit(sim: LevelSim, source: Tower | null): boolean {
  return sim.areaDepth > 0 || (source !== null && sim.balance.towers[source.archetype].area);
}

export function applyDamage(sim: LevelSim, e: Enemy, source: Tower | null, damage: Fix64, type: DamageType): void {
  // An immune unit (M36, sim/immunity.ts) takes nothing of its immune type: no hit at all, only the IMMUNE note.
  if (e.immunity !== 0 && immuneToDamage(e, type)) { noteImmune(sim, e, source, immunityForDamage(type)); return; }
  // Splash, chains and other area damage spread over a crowd; a brute or a boss shrugs most of it off (M30 #14).
  if (isHeavy(sim, e) && isAreaHit(sim, source)) damage = damage.mul(sim.balance.economy.areaVsHeavyMult);
  if (type === DamageType.Physical) {
    // A polymorphed sheep has no armour and no shield wall (sim/branches, Polymorph).
    let armour = e.sheepTicksLeft > 0 ? Fix64.Zero.sub(e.shredArmour) : e.armour.add(e.shieldArmour).sub(e.shredArmour);
    if (sim.mods.event !== null && !sim.mods.event.armour.isZero()) armour = armour.add(sim.mods.event.armour);   // MP-21 Boss Week tiers (sim/eventRun.ts)
    armour = Fix64.clamp(armour, MIN_ARMOUR, Fix64.One);
    damage = damage.mul(Fix64.One.sub(armour));
  }
  damage = affixedDamage(sim, e, damage, type);               // Warded, Fireproof (DESIGN §5.1)
  damage = resistedDamage(sim, e, source, damage, type);      // Spellward's magic resist (ENEMIES_V2)
  if (e.hexBonus.isPositive()) damage = damage.mul(Fix64.One.add(e.hexBonus));   // Hex Totem curse, every source (TOWERS_V2)
  if (sim.petSystem !== null && sim.petSystem.kit) damage = petDamageTaken(sim, e, source, damage);   // Pets v3: marks, Kindle, Night Eyes
  e.hp = e.hp.sub(damage);
  const sourceId = source?.id ?? 0;
  sim.events.add(SimEventKind.EnemyDamaged, e.id, sourceId, damage.floorToInt());
  maybeBoost(sim, e);
  e.hitsTaken++;
  const ea = sim.balance.enemies[e.archetype];
  if (ea.bossMechanic !== BossMechanic.None && e.hp.isPositive()) onBossHit(sim, e, ea.bossMechanic);
  if (e.hp.isPositive()) onHitByTower(sim, e, source, type);   // a Burrower dives (ENEMIES_V2)
  if (e.hp.le(Fix64.Zero)) {
    e.hp = Fix64.Zero;
    e.alive = false;
    sim._aliveEnemies--;
    sim.kills++;
    dropBomb(sim, e);
    let points = e.points;
    if (e.tolled) points = Fix64.fromInt(points).mul(sim.balance.cards.tollPointsMult).floorToInt();
    if (source !== null) {
      source.kills++;
      if (!source.auraPointsMult.isZero() && !source.auraPointsMult.eq(Fix64.One)) points = Fix64.fromInt(points).mul(source.auraPointsMult).floorToInt();
      const gp = sim.gear[source.archetype].points;
      if (!gp.isZero()) points = Fix64.fromInt(points).mul(Fix64.One.add(gp)).floorToInt();
    }
    if (sim.ascension.active) points = branchKillPoints(sim, e, points);      // Soul Harvest
    if (sim.petSystem !== null && sim.petSystem.kit) points = petKillPoints(sim, e, points);   // Pets v3: Glint, Shiny Find (points only)
    sim.events.add(SimEventKind.EnemyKilled, e.id, sourceId, points);
    sim.addPoints(points);
    if (isLiveRare(sim, e)) onRareKilled(sim, e);            // M36: the rare's chest, at once (sim/rares.ts)
    else if (isLiveWyrm(sim, e)) onWyrmKilled(sim, e);       // M40: the app grants its Starfall skin (sim/wyrm.ts)
    else if (isLiveWildPet(sim, e)) onWildPetCaught(sim, e); // Pets v2: caught; the app pays the catch (app pets.ts)
    if (sim.ascension.active) onBranchDeath(sim, e);                          // Doom Mark, Contagion, the Spirit Totem
    spreadBurnOnDeath(sim, e);                                 // Mythic "Wildfire"
    splitOnDeath(sim, e);                                      // elite "Splitting"
  }
}

export function canTarget(a: TowerArchetype, e: Enemy): boolean { return e.isAir ? a.targetsAir : a.targetsGround; }

function better(priority: TargetPriority, candidate: Enemy, current: Enemy): boolean {
  let cmp: number;
  switch (priority) {
    case TargetPriority.First: cmp = candidate.distance.cmp(current.distance); break;
    case TargetPriority.Last: cmp = current.distance.cmp(candidate.distance); break;
    case TargetPriority.Strongest: cmp = candidate.hp.cmp(current.hp); break;
    default: cmp = current.hp.cmp(candidate.hp); break;
  }
  if (cmp !== 0) return cmp > 0;
  return candidate.id < current.id;
}
